import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { formatDate, cn } from '../../lib/utils';
import { Plus, Search, AlertTriangle, User } from 'lucide-react';
import { toast } from 'sonner';

const ParticipantsPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [participants, setParticipants] = useState([]);
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  
  const [newParticipant, setNewParticipant] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    ndis_number: '',
    house_id: '',
    plan_start_date: '',
    plan_end_date: '',
    goals: [],
    support_categories: [],
    risk_alerts: [],
    medical_info: { gp: '', medications: [], allergies: [] },
    photo_consent: false,
    emergency_contacts: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [participantsRes, housesRes] = await Promise.all([
        api.get('/participants'),
        api.get('/houses')
      ]);
      setParticipants(participantsRes.data);
      setHouses(housesRes.data);
    } catch (error) {
      toast.error('Failed to load participants');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await api.post('/participants', newParticipant);
      toast.success('Participant created successfully');
      setCreateDialogOpen(false);
      setNewParticipant({
        first_name: '',
        last_name: '',
        date_of_birth: '',
        ndis_number: '',
        house_id: '',
        plan_start_date: '',
        plan_end_date: '',
        goals: [],
        support_categories: [],
        risk_alerts: [],
        medical_info: { gp: '', medications: [], allergies: [] },
        photo_consent: false,
        emergency_contacts: []
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create participant');
    }
  };

  const filteredParticipants = participants.filter(p => 
    `${p.first_name} ${p.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.ndis_number.includes(searchQuery)
  );

  const columns = [
    {
      header: 'Name',
      cell: (row) => (
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold">
            {row.first_name[0]}{row.last_name[0]}
          </div>
          <div>
            <p className="font-medium">{row.first_name} {row.last_name}</p>
            <p className="text-xs text-muted-foreground">NDIS: {row.ndis_number}</p>
          </div>
        </div>
      )
    },
    {
      header: 'House',
      accessor: 'house_name'
    },
    {
      header: 'Plan Period',
      cell: (row) => (
        <span className="text-sm">
          {formatDate(row.plan_start_date)} - {formatDate(row.plan_end_date)}
        </span>
      )
    },
    {
      header: 'Risk Alerts',
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.risk_alerts?.length > 0 ? (
            row.risk_alerts.slice(0, 2).map((alert, idx) => (
              <Badge key={idx} variant="outline" className="text-xs bg-amber-500/10 text-amber-600 border-amber-500/30">
                <AlertTriangle className="h-3 w-3 mr-1" />
                {alert.substring(0, 20)}...
              </Badge>
            ))
          ) : (
            <span className="text-muted-foreground text-xs">None</span>
          )}
        </div>
      )
    },
    {
      header: 'Actions',
      cell: (row) => (
        <div className="flex gap-2">
          <Button 
            variant="default" 
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              const basePath = user?.role === 'house_manager' ? '/manager' : '/admin';
              navigate(`${basePath}/participants/${row.id}`);
            }}
            data-testid={`view-profile-${row.id}`}
          >
            View Profile
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedParticipant(row);
              setViewDialogOpen(true);
            }}
            data-testid={`view-participant-${row.id}`}
          >
            Quick View
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="participants-page">
      <PageHeader 
        title="Participants"
        description="Manage participant profiles and NDIS details"
        actions={
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-participant-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Participant
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Participant</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>First Name *</Label>
                    <Input 
                      value={newParticipant.first_name}
                      onChange={(e) => setNewParticipant({...newParticipant, first_name: e.target.value})}
                      data-testid="participant-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name *</Label>
                    <Input 
                      value={newParticipant.last_name}
                      onChange={(e) => setNewParticipant({...newParticipant, last_name: e.target.value})}
                      data-testid="participant-last-name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date of Birth *</Label>
                    <Input 
                      type="date"
                      value={newParticipant.date_of_birth}
                      onChange={(e) => setNewParticipant({...newParticipant, date_of_birth: e.target.value})}
                      data-testid="participant-dob"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>NDIS Number *</Label>
                    <Input 
                      value={newParticipant.ndis_number}
                      onChange={(e) => setNewParticipant({...newParticipant, ndis_number: e.target.value})}
                      placeholder="430000000"
                      data-testid="participant-ndis"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>House *</Label>
                  <Select 
                    value={newParticipant.house_id} 
                    onValueChange={(v) => setNewParticipant({...newParticipant, house_id: v})}
                  >
                    <SelectTrigger data-testid="participant-house">
                      <SelectValue placeholder="Select house" />
                    </SelectTrigger>
                    <SelectContent>
                      {houses.map(h => (
                        <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Plan Start Date</Label>
                    <Input 
                      type="date"
                      value={newParticipant.plan_start_date}
                      onChange={(e) => setNewParticipant({...newParticipant, plan_start_date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Plan End Date</Label>
                    <Input 
                      type="date"
                      value={newParticipant.plan_end_date}
                      onChange={(e) => setNewParticipant({...newParticipant, plan_end_date: e.target.value})}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Risk Alerts (one per line)</Label>
                  <Textarea 
                    placeholder="Enter any risk alerts..."
                    onChange={(e) => setNewParticipant({
                      ...newParticipant, 
                      risk_alerts: e.target.value.split('\n').filter(Boolean)
                    })}
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreate} data-testid="save-participant-btn">Create Participant</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Search */}
      <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
        <CardContent className="py-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Search by name or NDIS number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="participant-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <DataTable 
        columns={columns}
        data={filteredParticipants}
        emptyMessage="No participants found"
      />

      {/* View Participant Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Participant Details</DialogTitle>
          </DialogHeader>
          {selectedParticipant && (
            <div className="space-y-6 py-4">
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary text-xl font-bold">
                  {selectedParticipant.first_name[0]}{selectedParticipant.last_name[0]}
                </div>
                <div>
                  <h3 className="text-xl font-semibold">
                    {selectedParticipant.first_name} {selectedParticipant.last_name}
                  </h3>
                  <p className="text-muted-foreground">NDIS: {selectedParticipant.ndis_number}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Date of Birth</Label>
                  <p>{formatDate(selectedParticipant.date_of_birth)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">House</Label>
                  <p>{selectedParticipant.house_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Plan Period</Label>
                  <p>{formatDate(selectedParticipant.plan_start_date)} - {formatDate(selectedParticipant.plan_end_date)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Photo Consent</Label>
                  <p>{selectedParticipant.photo_consent ? 'Yes' : 'No'}</p>
                </div>
              </div>

              {selectedParticipant.goals?.length > 0 && (
                <div>
                  <Label className="text-muted-foreground">Goals</Label>
                  <ul className="mt-2 space-y-1">
                    {selectedParticipant.goals.map((goal, idx) => (
                      <li key={idx} className="text-sm">• {goal}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedParticipant.risk_alerts?.length > 0 && (
                <div>
                  <Label className="text-muted-foreground flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    Risk Alerts
                  </Label>
                  <div className="mt-2 space-y-2">
                    {selectedParticipant.risk_alerts.map((alert, idx) => (
                      <div key={idx} className="p-2 rounded bg-amber-500/10 text-amber-700 dark:text-amber-300 text-sm">
                        {alert}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedParticipant.medical_info && (
                <div>
                  <Label className="text-muted-foreground">Medical Information</Label>
                  <div className="mt-2 p-4 rounded-lg bg-muted/30 space-y-2">
                    {selectedParticipant.medical_info.gp && (
                      <p><span className="font-medium">GP:</span> {selectedParticipant.medical_info.gp}</p>
                    )}
                    {selectedParticipant.medical_info.medications?.length > 0 && (
                      <p><span className="font-medium">Medications:</span> {selectedParticipant.medical_info.medications.join(', ')}</p>
                    )}
                    {selectedParticipant.medical_info.allergies?.length > 0 && (
                      <p><span className="font-medium">Allergies:</span> {selectedParticipant.medical_info.allergies.join(', ')}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParticipantsPage;
